import { useRef, useState } from "react";
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SignatureCanvas from "react-signature-canvas";

import Button from "../../shared/Button";
import { useDialogStyles } from "../../shared/dialog/styles";

const todayString = () => new Date().toISOString().slice(0, 10);

const useStyles = makeStyles({
  content: { display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 },
  signatureContainer: {
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: 4,
    width: "100%",
  },
  signatureCanvas: { width: "100%", height: 160 },
});

interface Props {
  open: boolean;
  padName?: string | null;
  // Seeded reference number (e.g. "CP3"). Kept in the payload for records and
  // reports but no longer shown to the QP — the dialog now mirrors the
  // stockpile approval dialog.
  sampleReferenceNumber: string;
  onCancel: () => void;
  onApprove: (
    sampleDate: string,
    sampleReferenceNumber: string,
    signature: string
  ) => void;
}

const ContainmentPadApprovalDialog = ({
  open,
  padName,
  sampleReferenceNumber,
  onCancel,
  onApprove,
}: Props) => {
  const classes = useStyles();
  const dialogClasses = useDialogStyles();
  const [sampleDate, setSampleDate] = useState<string>(todayString());
  const [signature, setSignature] = useState<string | null>(null);
  const sigCanvasRef = useRef<SignatureCanvas>(null);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle className={dialogClasses.dialogTitle}>
        QP Approval{padName ? ` — ${padName}` : ""}
      </DialogTitle>
      <DialogContent className={dialogClasses.dialogContent}>
        <Box className={classes.content}>
          <TextField
            label="Sample Date"
            type="date"
            value={sampleDate}
            onChange={(e) => setSampleDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
          />
          <Box>
            <Typography variant="body2" gutterBottom>
              QP Signature
            </Typography>
            <Box className={classes.signatureContainer}>
              <SignatureCanvas
                ref={sigCanvasRef}
                penColor="black"
                canvasProps={{ className: classes.signatureCanvas }}
                onEnd={() => {
                  if (sigCanvasRef.current) {
                    setSignature(sigCanvasRef.current.toDataURL("image/png"));
                  }
                }}
              />
            </Box>
            <MuiButton
              size="small"
              onClick={() => {
                sigCanvasRef.current?.clear();
                setSignature(null);
              }}
            >
              Clear
            </MuiButton>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={dialogClasses.dialogActions}>
        <div className={dialogClasses.actionButton}>
          <Button
            label="Cancel"
            onClick={onCancel}
            color="default"
            variant="contained"
            type="button"
          />
        </div>
        <div className={dialogClasses.actionButton} style={{ width: "auto" }}>
          <Button
            label="Approve & Close Pad"
            onClick={() =>
              signature &&
              onApprove(sampleDate, sampleReferenceNumber, signature)
            }
            color="primary"
            variant="contained"
            type="button"
            disabled={!signature}
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ContainmentPadApprovalDialog;
